import {
  Stethoscope, MessageCircle, Sparkles, Sun, Activity, Layers, Gem, Magnet, ScanFace,
  Baby, Star, Leaf, Smile, ShieldCheck, CircleDot, Contrast, Circle, Flower2,
  Hourglass, Scissors, ClipboardList, ArrowRight, ArrowUpRight,
} from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal, Img } from "./Reveal";
import { DENTAL_SERVICES, SKIN_SERVICES, IMG } from "@/data/content";
import { useBooking } from "@/lib/booking";
import { scrollToId } from "@/lib/scroll";

const ICONS = {
  Stethoscope, MessageCircle, Sparkles, Sun, Activity, Layers, Gem, Magnet, ScanFace,
  Baby, Star, Leaf, Smile, ShieldCheck, CircleDot, Contrast, Circle, Flower2,
  Hourglass, Scissors, ClipboardList,
};

function ServiceCard({ s, i, dark, openBooking }) {
  const Icon = ICONS[s.icon] || Sparkles;
  return (
    <Reveal delay={(i % 4) * 0.06}>
      <div
        data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`group h-full rounded-3xl border p-6 transition-all duration-500 hover:-translate-y-1.5 ${
          dark
            ? "border-ivory/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-gold/30"
            : "border-pine/10 bg-white shadow-soft hover:shadow-lift hover:border-teal-brand/30"
        }`}
      >
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl transition-colors duration-500 ${dark ? "bg-gold/15 text-gold" : "bg-teal-soft text-teal-brand group-hover:bg-pine group-hover:text-ivory"}`}>
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <h3 className={`mt-5 font-display text-lg font-semibold ${dark ? "text-ivory" : "text-pine-deep"}`}>{s.name}</h3>
        <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-ivory/60" : "text-slate-600"}`}>{s.desc}</p>
        <button
          data-testid={`service-learn-more-${i}`}
          onClick={() => openBooking(s.name)}
          className={`mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${dark ? "text-gold hover:text-gold-soft" : "text-teal-brand hover:text-pine"}`}
        >
          Learn More <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>
    </Reveal>
  );
}

export function DentalServices() {
  const { openBooking } = useBooking();
  return (
    <section id="dental" data-testid="dental-services-section" className="py-24 lg:py-32 bg-ivory">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            chapter="03"
            overline="Dental Care"
            title="Complete Dental Care"
            sub="From routine consultations to advanced restorative and cosmetic dentistry — every treatment begins with an honest conversation."
          />
          <Reveal delay={0.15}>
            <button
              data-testid="dental-explore-cta"
              onClick={() => openBooking("Dental Consultation")}
              className="group inline-flex items-center gap-2 rounded-full border border-pine/25 px-6 py-3 text-sm font-bold text-pine-deep transition-all duration-300 hover:bg-pine hover:text-ivory shrink-0"
            >
              Explore Dental Services <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {DENTAL_SERVICES.map((s, i) => (
            <ServiceCard key={s.name} s={s} i={i} dark={false} openBooking={openBooking} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function SkinServices() {
  const { openBooking } = useBooking();
  return (
    <section id="skin" data-testid="skin-services-section" className="py-24 lg:py-32 bg-pine-deep relative overflow-hidden grain">
      <div className="pointer-events-none absolute -top-40 -left-40 h-[30rem] w-[30rem] rounded-full bg-teal-brand/15 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <SectionHeading
              dark
              chapter="04"
              overline="Skin & Aesthetic Care"
              title="Advanced Skin & Aesthetic Care"
              sub="Doctor-guided skin consultations and care plans — measured, honest and built around your skin, never around pressure."
            />
            <Reveal delay={0.2}>
              <div className="mt-8 overflow-hidden rounded-3xl ring-1 ring-ivory/10 shadow-grand">
                <Img src={IMG.skinHero} alt="Professional skin care treatment" className="h-64 w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <button
                data-testid="skin-explore-cta"
                onClick={() => openBooking("Skin Consultation")}
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-bold text-pine-deep transition-all duration-300 hover:bg-gold-soft hover:-translate-y-0.5"
              >
                Explore Skin Care <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SKIN_SERVICES.map((s, i) => (
              <ServiceCard key={s.name} s={s} i={i} dark openBooking={openBooking} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
